import { useEffect, useState, useRef, useReducer } from 'react';
import { userAppContext } from '../../../../context/Context';
import { patchItems } from '@/components/Patches/PatchSearchItems';
import { LinksExamples } from './LinksExamples';
import SearchButton from '../../../../global/SearchButton/SearchButton';
import SharedButton from '../../../../global/Button/Button';
import type { Section as SectionType } from '../../../../types';
import SectionItem from './SectionItem';
import type { CancelTokenSource } from 'axios';
import { initialState, searchComponentReducer } from '../../reducers/searchComponentReducer';
import { fetchData } from '../apiCalls';
import { handleSearchItems } from '../helpers';

const Dropdown = ({ value, setValue, searchItems, setSearchItems, onDropdownToggle }) => {
  const [data, setData] = useState<any>(undefined);
  const [error, setError] = useState<string | null>(null);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const [state, dispatch] = useReducer(searchComponentReducer, initialState);
  const context = userAppContext();

  const noData =
    !data ||
    (data.diseases?.length === 0 &&
      data.genes?.length === 0 &&
      data.variants?.length === 0 &&
      data.chemicals?.length === 0);

  if (!context) {
    throw new Error('userAppContext is null – cannot proceed');
  }

  const { authorization, isAuthenticated } = context;
  const cancelToken = useRef(null);

  const handleSearch = async (value: string) => {
    setError('');
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const data = await fetchData({
        cancelToken,
        authorization,
        isAuthenticated,
        value,
      });
      setData(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  useEffect(() => {
    if (value.trim().length < 3) {
      setData(undefined);
      return;
    }

    const timeoutId = setTimeout(() => {
      handleSearch(value);
    }, 300);
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Cleanup');
    }
    return () => clearTimeout(timeoutId);
  }, [value, authorization, isAuthenticated]);

  useEffect(() => {
    onDropdownToggle(true);
    return () => onDropdownToggle(false);
  }, [value, onDropdownToggle]);

  const handleSelectAll = (sections: SectionType[]) => {
    setSearchItems(prev => {
      const newItems = patchItems(sections[0].data);
      return prev.concat(newItems.filter(item => !prev.some(prevItem => prevItem.id === item.id)));
    });
  };

  const itemContainerClass = searchItems
    ? 'w-full react-dropdown__section-container'
    : 'react-dropdown__section-container';

  const sections = noData ? [] : handleSearchItems(searchItems, data);

  return (
    <div id="react-autowhatever-1" className="react-dropdown-container" data-testid="dropdown-div">
      <div className="section-dropdown">
        {state.isLoading ? (
          <span>Loading...</span>
        ) : error ? (
          <span>{error.includes('403') ? 'Session expired. Please refresh page' : `Error: ${error}`}</span>
        ) : noData ? (
          <span>No results found</span>
        ) : (
          sections.map(section => (
            <div key={section.title} className={itemContainerClass}>
              <div className="react-dropdown__section-title">
                <strong>{section.title}</strong>
              </div>
              <ul role="listbox" className="react-dropdown-list">
                {section.data.map((item, index) => (
                  <SectionItem key={item.id ?? index} item={item} />
                ))}
              </ul>
            </div>
          ))
        )}
      </div>

      <div className="botonCab">
        {searchItems.length > 0 ? (
          <>
            <SharedButton className="BtnAccion" id="selectAll" onClick={() => handleSelectAll(sections)}>
              Select All
            </SharedButton>
            <SearchButton
              setValue={setValue}
              id="search-btn-text"
              className="BtnAccion custom-btn-search"
              size="sm"
              variant="primary"
            >
              Search
            </SearchButton>
            <SharedButton className="BtnAccion" id="clearAll" onClick={() => setSearchItems([])}>
              Clear All
            </SharedButton>
          </>
        ) : (
          <SharedButton className="BtnAccion" id="close" onClick={() => setValue('')}>
            Close
          </SharedButton>
        )}
      </div>
      {(noData || !searchItems.length) && <LinksExamples />}
    </div>
  );
};

export default Dropdown;
